import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  Brush,
  Camera,
  Code,
  Headphones,
  Mic,
  Pencil,
  BookOpen,
  Briefcase,
  Palette,
  Music,
  Video,
  MessageSquare,
  Sparkles,
  Zap,
  Coffee,
  Heart,
  Smile,
  Frown,
  Meh,
  User,
  Check
} from 'lucide-react';

interface AvatarGeneratorProps {
  onSelect: (avatarData: AvatarData) => void;
  initialAvatar?: AvatarData;
}

export interface AvatarData {
  bgColor: string;
  fgColor: string;
  icon: string;
  style: string;
}

const bgColors = [
  { name: 'Indigo', value: '#6366F1' },
  { name: 'Blue', value: '#3B82F6' },
  { name: 'Green', value: '#10B981' },
  { name: 'Red', value: '#EF4444' },
  { name: 'Purple', value: '#8B5CF6' },
  { name: 'Pink', value: '#EC4899' },
  { name: 'Yellow', value: '#F59E0B' },
  { name: 'Teal', value: '#14B8A6' },
  { name: 'Orange', value: '#F97316' },
  { name: 'Cyan', value: '#06B6D4' }
];

const fgColors = [
  { name: 'White', value: '#FFFFFF' },
  { name: 'Light Gray', value: '#F3F4F6' },
  { name: 'Yellow', value: '#FEF3C7' },
  { name: 'Light Blue', value: '#DBEAFE' },
  { name: 'Light Green', value: '#D1FAE5' },
  { name: 'Light Purple', value: '#EDE9FE' },
  { name: 'Light Pink', value: '#FCE7F3' }
];

const iconOptions = [
  { name: 'Photographer', value: 'camera', icon: Camera },
  { name: 'Developer', value: 'code', icon: Code },
  { name: 'Podcaster', value: 'mic', icon: Mic },
  { name: 'Writer', value: 'pencil', icon: Pencil },
  { name: 'Designer', value: 'palette', icon: Palette },
  { name: 'Musician', value: 'music', icon: Music },
  { name: 'Video Creator', value: 'video', icon: Video },
  { name: 'Educator', value: 'book', icon: BookOpen },
  { name: 'Business', value: 'briefcase', icon: Briefcase },
  { name: 'Artist', value: 'brush', icon: Brush },
  { name: 'Influencer', value: 'sparkles', icon: Sparkles },
  { name: 'Podcaster', value: 'headphones', icon: Headphones },
  { name: 'Blogger', value: 'messageSquare', icon: MessageSquare },
  { name: 'Energetic', value: 'zap', icon: Zap },
  { name: 'Casual', value: 'coffee', icon: Coffee },
  { name: 'Passionate', value: 'heart', icon: Heart },
  { name: 'Happy', value: 'smile', icon: Smile },
  { name: 'Serious', value: 'frown', icon: Frown },
  { name: 'Neutral', value: 'meh', icon: Meh },
  { name: 'Default', value: 'user', icon: User }
];

const styleOptions = [
  { name: 'Circle', value: 'circle' },
  { name: 'Rounded Square', value: 'rounded-square' },
  { name: 'Hexagon', value: 'hexagon' },
  { name: 'Diamond', value: 'diamond' },
  { name: 'Star', value: 'star' }
];

const AvatarGenerator: React.FC<AvatarGeneratorProps> = ({ onSelect, initialAvatar }) => {
  const [bgColor, setBgColor] = useState<string>(initialAvatar?.bgColor || bgColors[0].value);
  const [fgColor, setFgColor] = useState<string>(initialAvatar?.fgColor || fgColors[0].value);
  const [icon, setIcon] = useState<string>(initialAvatar?.icon || 'user');
  const [style, setStyle] = useState<string>(initialAvatar?.style || 'circle');
  const [step, setStep] = useState<number>(0);

  const handleComplete = () => {
    onSelect({
      bgColor,
      fgColor,
      icon,
      style
    });
  };

  const getIconComponent = (iconName: string) => {
    const iconOption = iconOptions.find(opt => opt.value === iconName);
    return iconOption ? iconOption.icon : User;
  };

  const renderAvatarPreview = () => {
    const IconComponent = getIconComponent(icon);
    
    let shapeClass = 'rounded-full'; // circle by default
    
    if (style === 'rounded-square') {
      shapeClass = 'rounded-xl';
    } else if (style === 'hexagon') {
      shapeClass = 'clip-path-hexagon';
    } else if (style === 'diamond') {
      shapeClass = 'rotate-45';
    } else if (style === 'star') {
      shapeClass = 'clip-path-star';
    }
    
    return (
      <div className="flex flex-col items-center justify-center mb-6">
        <div 
          className={`w-24 h-24 flex items-center justify-center ${shapeClass} overflow-hidden`}
          style={{ backgroundColor: bgColor }}
        >
          <div className={style === 'diamond' ? '-rotate-45' : ''}>
            <IconComponent className="h-12 w-12" style={{ color: fgColor }} />
          </div>
        </div>
        <p className="mt-3 text-sm text-gray-600 dark:text-gray-400">Preview</p>
      </div>
    );
  };

  const renderStepContent = () => {
    switch (step) {
      case 0: // Background Color
        return (
          <>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Choose Background Color</h3>
            <div className="grid grid-cols-5 gap-3">
              {bgColors.map((color) => (
                <button
                  key={color.value}
                  onClick={() => setBgColor(color.value)}
                  className={`w-10 h-10 rounded-full transition-all ${
                    bgColor === color.value ? 'ring-2 ring-offset-2 ring-gray-400 dark:ring-gray-600 scale-110' : 'hover:scale-110'
                  }`}
                  style={{ backgroundColor: color.value }}
                  title={color.name}
                >
                  {bgColor === color.value && (
                    <Check className="h-5 w-5 text-white mx-auto drop-shadow-md" />
                  )}
                </button>
              ))}
            </div>
          </>
        );
        
      case 1: // Foreground Color (Icon Color)
        return (
          <>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Choose Icon Color</h3>
            <div className="grid grid-cols-5 gap-3">
              {fgColors.map((color) => (
                <button
                  key={color.value}
                  onClick={() => setFgColor(color.value)}
                  className={`w-10 h-10 rounded-full transition-all ${
                    fgColor === color.value ? 'ring-2 ring-offset-2 ring-gray-400 dark:ring-gray-600 scale-110' : 'hover:scale-110'
                  }`}
                  style={{ backgroundColor: color.value }}
                  title={color.name}
                >
                  {fgColor === color.value && (
                    <Check className="h-5 w-5 text-gray-700 mx-auto drop-shadow-sm" />
                  )}
                </button>
              ))}
            </div>
          </>
        );
        
      case 2: // Icon Selection
        return (
          <>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Choose Your Icon</h3>
            <div className="grid grid-cols-4 gap-3 max-h-60 overflow-y-auto pr-2">
              {iconOptions.map((iconOption) => {
                const IconComponent = iconOption.icon;
                return (
                  <button
                    key={iconOption.value}
                    onClick={() => setIcon(iconOption.value)}
                    className={`p-3 rounded-lg flex flex-col items-center justify-center transition-all ${
                      icon === iconOption.value 
                        ? 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 ring-2 ring-indigo-500'
                        : 'bg-gray-100 dark:bg-dark-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-dark-600'
                    }`}
                    title={iconOption.name}
                  >
                    <IconComponent className="h-6 w-6 mb-1" />
                    <span className="text-xs font-medium">{iconOption.name}</span>
                  </button>
                );
              })}
            </div>
          </>
        );
        
      case 3: // Shape Style
        return (
          <>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Choose Avatar Shape</h3>
            <div className="grid grid-cols-3 gap-4">
              {styleOptions.map((styleOption) => {
                let previewClass = 'rounded-full'; // circle
                
                if (styleOption.value === 'rounded-square') {
                  previewClass = 'rounded-xl';
                } else if (styleOption.value === 'hexagon') {
                  previewClass = 'clip-path-hexagon';
                } else if (styleOption.value === 'diamond') {
                  previewClass = 'rotate-45';
                } else if (styleOption.value === 'star') {
                  previewClass = 'clip-path-star';
                }
                
                const IconComponent = getIconComponent(icon);
                
                return (
                  <button
                    key={styleOption.value}
                    onClick={() => setStyle(styleOption.value)}
                    className={`p-4 rounded-lg flex flex-col items-center justify-center transition-all ${
                      style === styleOption.value 
                        ? 'bg-indigo-50 dark:bg-indigo-900/20 border-2 border-indigo-500 dark:border-indigo-400'
                        : 'bg-gray-50 dark:bg-dark-700 border border-gray-200 dark:border-dark-600 hover:bg-gray-100 dark:hover:bg-dark-600'
                    }`}
                  >
                    <div 
                      className={`w-16 h-16 flex items-center justify-center ${previewClass} overflow-hidden`}
                      style={{ backgroundColor: bgColor }}
                    >
                      <div className={styleOption.value === 'diamond' ? '-rotate-45' : ''}>
                        <IconComponent className="h-8 w-8" style={{ color: fgColor }} />
                      </div>
                    </div>
                    <span className="mt-2 text-xs font-medium text-gray-700 dark:text-gray-300">
                      {styleOption.name}
                    </span>
                  </button>
                );
              })}
            </div>
          </>
        );
        
      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      {renderAvatarPreview()}
      
      <div className="space-y-6">
        {renderStepContent()}
      </div>
      
      <div className="flex justify-between pt-4 border-t border-gray-200 dark:border-dark-700">
        <button
          onClick={() => setStep(prev => Math.max(0, prev - 1))}
          disabled={step === 0}
          className={`px-4 py-2 rounded-lg transition-colors ${
            step === 0
              ? 'text-gray-400 dark:text-gray-600 cursor-not-allowed'
              : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-dark-700'
          }`}
        >
          Back
        </button>
        
        {step < 3 ? (
          <button
            onClick={() => setStep(prev => prev + 1)}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
          >
            Next
          </button>
        ) : (
          <button
            onClick={handleComplete}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
          >
            Save Avatar
          </button>
        )}
      </div>
    </div>
  );
};

export default AvatarGenerator;